import React, { useState, useEffect } from 'react';
import { FileText, TrendingUp, Calendar, Download } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/authService';

const CashFlowReports = () => {
  const [activeTab, setActiveTab] = useState('dre');
  const [loading, setLoading] = useState(false);
  const [dreData, setDreData] = useState(null);
  const [projectionData, setProjectionData] = useState([]);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(1);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() + 1);
    date.setDate(0);
    return date.toISOString().split('T')[0];
  });

  useEffect(() => {
    if (activeTab === 'dre') {
      loadDRE();
    } else {
      loadProjection();
    }
  }, [activeTab, startDate, endDate]);

  const loadDRE = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cash-flow/reports/dre', {
        params: { start_date: startDate, end_date: endDate }
      });
      setDreData(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar DRE:', error);
      toast.error('Erro ao carregar relatório DRE');
    } finally {
      setLoading(false);
    }
  };

  const loadProjection = async () => {
    try {
      setLoading(true);
      const response = await api.get('/cash-flow/projection', {
        params: { start_date: startDate, end_date: endDate }
      });
      setProjectionData(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar projeção:', error);
      toast.error('Erro ao carregar projeção de fluxo');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Relatórios Financeiros</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Análises e projeções do fluxo de caixa
        </p>
      </div>

      {/* Filtros e Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Tabs */}
          <div className="flex gap-2">
            <button
              onClick={() => setActiveTab('dre')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'dre'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <FileText className="w-5 h-5 inline mr-2" />
              DRE
            </button>
            <button
              onClick={() => setActiveTab('projection')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                activeTab === 'projection'
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
              }`}
            >
              <TrendingUp className="w-5 h-5 inline mr-2" />
              Fluxo Projetado
            </button>
          </div>

          {/* Filtro de Período */}
          <div className="flex gap-2">
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Data Inicial</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Data Final</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Conteúdo */}
      {activeTab === 'dre' && dreData && (
        <div className="space-y-6">
          {/* Resumo */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-green-500 rounded-xl p-6 text-white shadow-lg">
              <p className="text-sm opacity-80 mb-2">Receitas Totais</p>
              <p className="text-3xl font-bold">{formatCurrency(dreData.income.total)}</p>
            </div>

            <div className="bg-red-500 rounded-xl p-6 text-white shadow-lg">
              <p className="text-sm opacity-80 mb-2">Despesas Totais</p>
              <p className="text-3xl font-bold">{formatCurrency(dreData.expense.total)}</p>
            </div>

            <div className={`rounded-xl p-6 text-white shadow-lg ${
              dreData.result >= 0 ? 'bg-blue-500' : 'bg-orange-500'
            }`}>
              <p className="text-sm opacity-80 mb-2">Resultado</p>
              <p className="text-3xl font-bold">{formatCurrency(dreData.result)}</p>
              <p className="text-sm opacity-80 mt-2">
                {dreData.result >= 0 ? 'Lucro' : 'Prejuízo'}
              </p>
            </div>
          </div>

          {/* Detalhamento */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Receitas */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full" />
                Receitas por Categoria
              </h3>

              <div className="space-y-3">
                {dreData.income.categories.map((cat, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{cat.category}</span>
                    <span className="text-sm font-semibold text-green-600">
                      {formatCurrency(cat.amount)}
                    </span>
                  </div>
                ))}

                {dreData.income.categories.length === 0 && (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                    Nenhuma receita no período
                  </p>
                )}
              </div>
            </div>

            {/* Despesas */}
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full" />
                Despesas por Categoria
              </h3>

              <div className="space-y-3">
                {dreData.expense.categories.map((cat, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                    <span className="text-sm text-gray-700 dark:text-gray-300">{cat.category}</span>
                    <span className="text-sm font-semibold text-red-600">
                      {formatCurrency(cat.amount)}
                    </span>
                  </div>
                ))}

                {dreData.expense.categories.length === 0 && (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-4">
                    Nenhuma despesa no período
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Período */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300">
              <Calendar className="w-5 h-5" />
              <span className="font-medium">
                Período: {formatDate(dreData.period.start_date)} até {formatDate(dreData.period.end_date)}
              </span>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'projection' && projectionData.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Data
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Receitas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Despesas
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Saldo Confirmado
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Receitas Pendentes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Despesas Pendentes
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                    Saldo Projetado
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {projectionData.map((day, index) => (
                  <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatDate(day.date)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-600 font-medium">
                      {formatCurrency(day.income_completed)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-600 font-medium">
                      {formatCurrency(day.expense_completed)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right font-bold text-blue-600">
                      {formatCurrency(day.balance_completed)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-green-500">
                      {formatCurrency(day.income_pending)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-red-500">
                      {formatCurrency(day.expense_pending)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right font-bold ${
                      day.balance_projected >= 0 ? 'text-blue-600' : 'text-orange-600'
                    }`}>
                      {formatCurrency(day.balance_projected)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {projectionData.length === 0 && !loading && (
            <div className="text-center py-12">
              <TrendingUp className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500 dark:text-gray-400">Nenhuma projeção disponível para o período</p>
            </div>
          )}
        </div>
      )}

      {loading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
        </div>
      )}
    </div>
  );
};

export default CashFlowReports;